import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useHeader } from '@/contexts/HeaderContext';

const Terms = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isItalian = i18n.language?.startsWith('it');

  const { setHeaderProps } = useHeader();
  useEffect(() => {
    setHeaderProps({ title: isItalian ? 'Termini di Servizio' : 'Terms of Service' });
  }, [isItalian, setHeaderProps]);

  const termsContentIT = {
    lastUpdated: "Ultimo aggiornamento: 2 Gennaio 2025",
    sections: [
      {
        title: "1. Accettazione dei Termini",
        content: "Utilizzando l'applicazione Better Husband (\"App\"), accetti di essere vincolato da questi Termini di Servizio. Se non accetti questi termini, non utilizzare l'App."
      },
      {
        title: "2. Descrizione del Servizio",
        content: "Better Husband è un'applicazione progettata per aiutare gli utenti a migliorare le loro relazioni attraverso suggerimenti personalizzati generati dall'intelligenza artificiale. L'App offre:",
        list: [
          "Suggerimenti giornalieri personalizzati",
          "Funzionalità di note e diario",
          "Gestione eventi e promemoria",
          "Statistiche e tracciamento dei progressi"
        ]
      },
      {
        title: "3. Account Utente",
        content: "Per utilizzare l'App, devi creare un account fornendo informazioni accurate e complete. Sei responsabile di:",
        list: [
          "Mantenere la riservatezza delle tue credenziali",
          "Tutte le attività che si verificano sotto il tuo account",
          "Notificarci immediatamente di qualsiasi uso non autorizzato"
        ]
      },
      {
        title: "4. Accesso a vita",
        content: "L'accesso alle funzionalità dell'App richiede l'acquisto dell'accesso a vita: un pagamento unico, senza abbonamento e senza rinnovo automatico. Non c'è nulla da annullare. I prezzi possono variare in base alla regione e alla valuta. Gli abbonamenti sottoscritti prima del passaggio all'accesso a vita restano validi alle condizioni originarie fino alla loro scadenza o cancellazione."
      },
      {
        title: "5. Contenuti dell'Utente",
        content: "Mantieni la proprietà di tutti i contenuti che crei nell'App (note, feedback, ecc.). Concedi a Better Husband una licenza limitata per utilizzare questi contenuti al fine di fornire e migliorare il servizio."
      },
      {
        title: "6. Uso Accettabile",
        content: "Accetti di non:",
        list: [
          "Utilizzare l'App per scopi illegali",
          "Tentare di accedere ad account di altri utenti",
          "Interferire con il funzionamento dell'App",
          "Utilizzare l'App per molestare o danneggiare altri"
        ]
      },
      {
        title: "7. Limitazioni dell'IA",
        content: "I suggerimenti generati dall'IA sono intesi come guida generale e non sostituiscono la consulenza professionale. Non garantiamo che i suggerimenti siano adatti alla tua situazione specifica. Per problemi seri nella relazione, consigliamo di consultare un professionista."
      },
      {
        title: "8. Limitazione di Responsabilità",
        content: "L'App viene fornita \"così com'è\" senza garanzie di alcun tipo. Non siamo responsabili per danni indiretti, incidentali o consequenti derivanti dall'uso dell'App."
      },
      {
        title: "9. Modifiche ai Termini",
        content: "Possiamo modificare questi termini in qualsiasi momento. Le modifiche saranno effettive al momento della pubblicazione nell'App. L'uso continuato dell'App dopo le modifiche costituisce accettazione dei nuovi termini."
      },
      {
        title: "10. Legge Applicabile",
        content: "Questi termini sono regolati dalle leggi italiane. Qualsiasi controversia sarà sottoposta alla giurisdizione esclusiva dei tribunali italiani."
      },
      {
        title: "11. Contatti",
        content: "Per domande sui Termini di Servizio, contattaci a: support.betterhusband@gmail.com"
      }
    ]
  };

  const termsContentEN = {
    lastUpdated: "Last updated: January 2, 2025",
    sections: [
      {
        title: "1. Acceptance of Terms",
        content: "By using the Better Husband application (\"App\"), you agree to be bound by these Terms of Service. If you do not accept these terms, do not use the App."
      },
      {
        title: "2. Service Description",
        content: "Better Husband is an application designed to help users improve their relationships through personalized AI-generated suggestions. The App offers:",
        list: [
          "Personalized daily suggestions",
          "Notes and journal features",
          "Event management and reminders",
          "Statistics and progress tracking"
        ]
      },
      {
        title: "3. User Account",
        content: "To use the App, you must create an account by providing accurate and complete information. You are responsible for:",
        list: [
          "Maintaining the confidentiality of your credentials",
          "All activities that occur under your account",
          "Notifying us immediately of any unauthorized use"
        ]
      },
      {
        title: "4. Lifetime Access",
        content: "Access to app features requires purchasing lifetime access: a single one-time payment, with no subscription and no automatic renewal. There is nothing to cancel. Prices may vary by region and currency. Subscriptions started before the move to lifetime access remain valid on their original terms until they expire or are cancelled."
      },
      {
        title: "5. User Content",
        content: "You retain ownership of all content you create in the App (notes, feedback, etc.). You grant Better Husband a limited license to use this content to provide and improve the service."
      },
      {
        title: "6. Acceptable Use",
        content: "You agree not to:",
        list: [
          "Use the App for illegal purposes",
          "Attempt to access other users' accounts",
          "Interfere with the App's operation",
          "Use the App to harass or harm others"
        ]
      },
      {
        title: "7. AI Limitations",
        content: "AI-generated suggestions are intended as general guidance and do not replace professional advice. We do not guarantee that suggestions are suitable for your specific situation. For serious relationship issues, we recommend consulting a professional."
      },
      {
        title: "8. Limitation of Liability",
        content: "The App is provided \"as is\" without warranties of any kind. We are not liable for indirect, incidental, or consequential damages arising from use of the App."
      },
      {
        title: "9. Changes to Terms",
        content: "We may modify these terms at any time. Changes will be effective upon posting in the App. Continued use of the App after changes constitutes acceptance of the new terms."
      },
      {
        title: "10. Governing Law",
        content: "These terms are governed by Italian law. Any disputes shall be subject to the exclusive jurisdiction of Italian courts."
      },
      {
        title: "11. Contact",
        content: "For questions about the Terms of Service, contact us at: support.betterhusband@gmail.com"
      }
    ]
  };

  const content = isItalian ? termsContentIT : termsContentEN;

  return (
    <div style={{ background: 'hsl(var(--background))', display: 'flex', justifyContent: 'center', height: '100dvh', overflow: 'hidden' }}>
      <div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', background: 'hsl(var(--background))', position: 'relative', paddingBottom: 80, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ padding: '24px 24px 0' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, stroke: '#000', fill: 'none', strokeWidth: 1.5 }}><polyline points="15 18 9 12 15 6"/></svg>
            <span style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 18, color: '#000' }}>{i18n.language?.startsWith('it') ? 'Indietro' : i18n.language?.startsWith('es') ? 'Atrás' : 'Back'}</span>
          </button>
        </div>

        <div style={{ height: 1, background: 'rgba(0,0,0,0.08)', margin: '12px 24px' }} />

        {/* Content */}
        <div className="rounded-2xl p-6" style={{ background: 'hsl(var(--background))', color: '#000', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 20px 40px rgba(0,0,0,0.06)' }}>
          <div className="mb-8">
            <h2 className="text-2xl font-bold" style={{ color: '#000' }}>{isItalian ? 'Termini di Servizio' : 'Terms of Service'}</h2>
            <p className="text-sm mt-2" style={{ color: 'rgba(0,0,0,0.7)' }}>{content.lastUpdated}</p>
          </div>
          
          <div className="space-y-8">
            {content.sections.map((section, index) => (
              <div key={index}>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#000' }}>{section.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(0,0,0,0.8)' }}>{section.content}</p>
                {section.list && (
                  <ul className="mt-3 space-y-2">
                    {section.list.map((item, i) => (
                      <li key={i} className="text-sm flex items-start gap-2" style={{ color: 'rgba(0,0,0,0.8)' }}>
                        <span className="mt-1" style={{ color: '#5C8748' }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
